import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Plus, Pencil, Trash2, GraduationCap, Clock, Award, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultTemplate = {
  id: '',
  name: '',
  title: '',
  description: '',
  training_type: 'external',
  provider: '',
  duration_hours: 8,
  certification_required: true,
  certification_name: '',
  certification_validity_months: 60,
  pass_criteria: '',
  passing_score: 70
};

const TrainingTemplatesSettings = () => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState(defaultTemplate);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings/training-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error(t('auto.set.failedToLoadTrainingTemplates'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({ ...template });
    } else {
      setEditingTemplate(null);
      setFormData({ ...defaultTemplate, id: '' });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.title) {
      toast.error(t('auto.set.nameAndTitleAreRequired'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (editingTemplate) {
        await axios.put(
          `${API}/settings/training-templates/${editingTemplate.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(t('auto.set.templateUpdatedSuccessfully'));
      } else {
        await axios.post(
          `${API}/settings/training-templates`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(t('auto.set.templateCreatedSuccessfully'));
      }
      
      setDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm(t('auto.set.areYouSureYouWantTo2'))) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/settings/training-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('auto.set.templateDeleted'));
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error(t('auto.set.failedToDeleteTemplate'));
    }
  };

  if (loading) {
    return <div className="p-4 text-center">{t('auto.set.loadingTemplates')}</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-purple-600" />
                {t('auto.set.trainingTemplates')}
              </CardTitle>
              <CardDescription>
                {t('auto.set.predefineTrainingConfigurationsForConsistentData')}
              </CardDescription>
            </div>
            <Button onClick={() => handleOpenDialog()} data-testid="add-training-template-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t('auto.set.addTemplate')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              No training templates defined. Click "Add Template" to create one.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <Card key={template.id} className="bg-gray-50 border">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold text-lg">{template.name}</h3>
                        <p className="text-sm text-gray-600">{template.title}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(template)}
                          data-testid={`edit-template-${template.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(template.id)}
                          data-testid={`delete-template-${template.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                      {template.description || 'No description'}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 mb-2">
                      <Badge variant="outline" className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {template.duration_hours}h
                      </Badge>
                      <Badge variant="outline">
                        {template.training_type}
                      </Badge>
                      {template.certification_required && (
                        <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
                          <Award className="h-3 w-3" />
                          {t('auto.set.certificate')}
                        </Badge>
                      )}
                      {template.passing_score && (
                        <Badge variant="outline" className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          {template.passing_score}%
                        </Badge>
                      )}
                    </div>
                    
                    {template.certification_validity_months && (
                      <p className="text-xs text-gray-500">
                        Validity: {template.certification_validity_months} months
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? t('auto.set.editTrainingTemplate') : t('auto.set.addTrainingTemplate')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>{t('auto.set.templateName')}</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value  }))}
                  placeholder="e.g., OSHA-10"
                  data-testid="template-name-input"
                />
                <p className="text-xs text-gray-500 mt-1">{t('auto.set.shortIdentifierShownInDropdown')}</p>
              </div>
              <div>
                <Label>{t('auto.set.trainingType')}</Label>
                <Select
                  value={formData.training_type}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, training_type: v  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">{t('auto.set.internal')}</SelectItem>
                    <SelectItem value="external">{t('auto.set.external')}</SelectItem>
                    <SelectItem value="online">{t('auto.set.online')}</SelectItem>
                    <SelectItem value="on_the_job">{t('auto.set.onTheJob')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>{t('auto.set.fullTitle')}</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value  }))}
                placeholder="e.g., OSHA 10-Hour Safety Training"
                data-testid="template-title-input"
              />
            </div>
            
            <div>
              <Label>{t('auto.set.description')}</Label>
              <textarea
                className="w-full min-h-[80px] p-2 border rounded-md text-sm"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value  }))}
                placeholder={t('auto.set.describeTheTrainingContentAndObjectives')}
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>{t('auto.set.provider')}</Label>
                <Input
                  value={formData.provider}
                  onChange={(e) => setFormData(prev => ({ ...prev, provider: e.target.value  }))}
                  placeholder={t('auto.set.trainingProviderAgency')}
                />
              </div>
              <div>
                <Label>{t('auto.set.durationHours')}</Label>
                <Input
                  type="number"
                  value={formData.duration_hours}
                  onChange={(e) => setFormData(prev => ({ ...prev, duration_hours: parseFloat(e.target.value) || 0  }))}
                  min="0"
                  step="0.5"
                />
              </div>
            </div>
            
            {/* Certification Section */}
            <div className="p-4 bg-gray-50 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium">{t('auto.set.certificationRequired')}</Label>
                <Switch
                  checked={formData.certification_required}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, certification_required: checked  }))}
                />
              </div>
              
              {formData.certification_required && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  <div>
                    <Label>{t('auto.set.certificateName')}</Label>
                    <Input
                      value={formData.certification_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, certification_name: e.target.value  }))}
                      placeholder="e.g., OSHA-10 Card"
                    />
                  </div>
                  <div>
                    <Label>{t('auto.set.validityMonths')}</Label>
                    <Input
                      type="number"
                      value={formData.certification_validity_months}
                      onChange={(e) => setFormData(prev => ({ ...prev, certification_validity_months: parseInt(e.target.value) || 0  }))}
                      min="0"
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Assessment Section */}
            <div className="p-4 bg-blue-50 rounded-lg space-y-3">
              <h4 className="font-medium text-blue-800">{t('auto.set.assessmentSettings')}</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t('auto.set.passingScore')}</Label>
                  <Input
                    type="number"
                    value={formData.passing_score}
                    onChange={(e) => setFormData(prev => ({ ...prev, passing_score: parseFloat(e.target.value) || 0  }))}
                    min="0"
                    max="100"
                  />
                </div>
                <div>
                  <Label>{t('auto.set.passCriteria')}</Label>
                  <Input
                    value={formData.pass_criteria}
                    onChange={(e) => setFormData(prev => ({ ...prev, pass_criteria: e.target.value  }))}
                    placeholder="e.g., Complete all modules"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="dialog-cancel-btn">
              {t('auto.set.cancel')}
            </Button>
            <Button onClick={handleSave} data-testid="save-template-btn">
              {editingTemplate ? t('auto.set.updateTemplate') : t('auto.set.createTemplate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainingTemplatesSettings;
